/**
 * CostBreakdownChart Component
 * Pie chart showing API costs by provider
 */

import { useSnapshot } from "valtio";
import { financialStore, formatCurrency } from "../stores/financial";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";

// Provider colors
const PROVIDER_COLORS: Record<string, string> = {
  openai: "#10a37f", // OpenAI green
  anthropic: "#cc785c", // Anthropic coral
  openrouter: "#667eea", // Purple
  elevenlabs: "#000000", // Black
  workers_ai: "#f38020", // Cloudflare orange
};

const DEFAULT_COLOR = "#6b7280"; // Gray

export default function CostBreakdownChart() {
  const { costBreakdown, loading } = useSnapshot(financialStore);

  if (loading && !costBreakdown) {
    return <Skeleton className="h-[350px]" />;
  }

  if (!costBreakdown || costBreakdown.byProvider.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cost by Provider</CardTitle>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <p className="text-muted-foreground">No cost data available</p>
        </CardContent>
      </Card>
    );
  }

  const data = costBreakdown.byProvider.map((p) => ({
    name: formatProviderName(p.provider),
    value: p.cost,
    percentage: p.percentage,
    requests: p.requests,
    provider: p.provider,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cost by Provider</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <RechartsPieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
              dataKey="value"
              label={({ name, percentage }) => `${name} (${percentage}%)`}
            >
              {data.map((entry) => (
                <Cell
                  key={`cell-${entry.provider}`}
                  fill={PROVIDER_COLORS[entry.provider] ?? DEFAULT_COLOR}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
              }}
              formatter={(value: number, name: string, entry) => {
                const item = entry.payload as (typeof data)[0];
                return [
                  <div key="tooltip" className="space-y-1">
                    <div className="font-medium">{formatCurrency(value)}</div>
                    <div className="text-xs text-muted-foreground">
                      {item.requests.toLocaleString()} requests
                    </div>
                  </div>,
                  name,
                ];
              }}
            />
            <Legend />
          </RechartsPieChart>
        </ResponsiveContainer>
        <div className="mt-4 text-center">
          <p className="text-sm text-muted-foreground">
            Total: {formatCurrency(costBreakdown.totalCost)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function formatProviderName(provider: string): string {
  const names: Record<string, string> = {
    openai: "OpenAI",
    anthropic: "Anthropic",
    openrouter: "OpenRouter",
    elevenlabs: "ElevenLabs",
    workers_ai: "Workers AI",
  };
  return names[provider] ?? provider;
}
